/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type {GitHubPullRequestReviewThreadsByLine} from './github/pullRequestTimelineTypes';

import InlineCommentThread from './InlineCommentThread';
import PullRequestNewCommentInput from './PullRequestNewCommentInput';
import {DiffSide} from './generated/graphql';
import {
  gitHubPullRequestCanAddCommentAtom,
  gitHubPullRequestLineInCommentRangeAtom,
  gitHubPullRequestLineInHoveredThreadAtom,
  gitHubPullRequestLineInSelectedRangeAtom,
  gitHubPullRequestNewCommentInputCellAtom,
  gitHubPullRequestNewCommentInputShownAtom,
} from './jotai';

import {useAtomValue} from 'jotai';
import {useMemo} from 'react';

type Props = {
  beforeLineNumber: number | null;
  before: React.ReactFragment | null;
  afterLineNumber: number | null;
  after: React.ReactFragment | null;
  rowType: SplitDiffRowType;
  path: string;
  threads: {
    before: GitHubPullRequestReviewThreadsByLine;
    after: GitHubPullRequestReviewThreadsByLine;
  };
};

type SplitDiffRowType = 'add' | 'common' | 'modify' | 'remove' | 'expanded';

export default function SplitDiffRow({
  beforeLineNumber,
  before,
  afterLineNumber,
  after,
  rowType,
  path,
  threads,
}: Props): React.ReactElement {
  let beforeClass;
  let afterClass;
  switch (rowType) {
    case 'remove':
      beforeClass = 'patch-remove-line';
      afterClass = undefined;
      break;
    case 'modify':
      beforeClass = 'patch-remove-line';
      afterClass = 'patch-add-line';
      break;
    case 'add':
      beforeClass = undefined;
      afterClass = 'patch-add-line';
      break;
    case 'common':
      beforeClass = undefined;
      afterClass = undefined;
      break;
    case 'expanded':
      beforeClass = 'patch-expanded';
      afterClass = 'patch-expanded';
      break;
  }

  // Note that 'expanded' is a special case of 'common' where it is code that is
  // common to both sides of the diff, but was previously displayed as
  // collapsed. For whatever reason, GitHub does not make it possible to comment
  // on lines outside of the patch contents in PRs:
  //
  // https://github.com/isaacs/github/issues/1655
  //
  // Even if you try to do so programmatically via the GraphQL API, it *still*
  // doesn't work, so this seems to be some quirk in the underlying data model.
  const canComment = rowType !== 'expanded';

  return (
    <tr>
      <SplitDiffRowSide
        className={beforeClass}
        content={before}
        lineNumber={beforeLineNumber}
        path={path}
        side={DiffSide.Left}
        threads={threads.before}
        canComment={canComment}
      />
      <SplitDiffRowSide
        className={afterClass}
        content={after}
        lineNumber={afterLineNumber}
        path={path}
        side={DiffSide.Right}
        threads={threads.after}
        canComment={canComment}
      />
    </tr>
  );
}

type SideProps = {
  className?: string;
  content: React.ReactFragment | null;
  lineNumber: number | null;
  path: string;
  side: DiffSide;
  threads: GitHubPullRequestReviewThreadsByLine;
  canComment: boolean;
};

function SplitDiffRowSide({
  className,
  content,
  lineNumber,
  path,
  side,
  threads,
  canComment,
}: SideProps) {
  const param = useMemo(() => ({path, lineNumber, side}), [path, lineNumber, side]);

  // These atoms are synchronous derived atoms that read from data preloaded
  // by useSplitDiffViewData in <SplitDiffView>.
  const canAddCommentAtom = useMemo(
    () => gitHubPullRequestCanAddCommentAtom(param),
    [param],
  );
  const newCommentInputShownAtom = useMemo(
    () => gitHubPullRequestNewCommentInputShownAtom(param),
    [param],
  );
  const lineInRangeAtom = useMemo(
    () => gitHubPullRequestLineInCommentRangeAtom(param),
    [param],
  );
  const lineInHoveredThreadAtom = useMemo(
    () => gitHubPullRequestLineInHoveredThreadAtom(param),
    [param],
  );
  const lineInSelectedRangeAtom = useMemo(
    () => gitHubPullRequestLineInSelectedRangeAtom(param),
    [param],
  );

  const canAddCommentValue = useAtomValue(canAddCommentAtom);
  const isNewCommentInputShownValue = useAtomValue(newCommentInputShownAtom);
  const isInCommentRange = useAtomValue(lineInRangeAtom);
  const isInHoveredThread = useAtomValue(lineInHoveredThreadAtom);
  const isInSelectedRange = useAtomValue(lineInSelectedRangeAtom);

  // Only use the values if commenting is allowed for this row
  const canAddComment = canComment && canAddCommentValue;
  const isNewCommentInputShown = canComment && isNewCommentInputShownValue;

  let commentThreads = null;
  let input = null;
  if (lineNumber != null) {
    commentThreads = <SplitDiffRowCommentThreads line={lineNumber} threadsByLine={threads} />;

    if (isNewCommentInputShown) {
      input = <NewCommentInputWithCell line={lineNumber} path={path} side={side} />;
    }
  }

  const commentHighlighted = isInCommentRange || isInHoveredThread;
  const lineHighlighted = isInSelectedRange;

  const lineNumberClasses = [
    'lineNumber',
    className != null && `${className}-number`,
    canAddComment && 'commentable',
    commentHighlighted && 'comment-range-highlight',
    lineHighlighted && 'line-selection-highlight',
    side === 'RIGHT' && 'right-side',
  ].filter(Boolean).join(' ');

  const contentClassName = [
    className,
    commentHighlighted && 'comment-range-selected',
    lineHighlighted && 'line-selection-highlighted',
    isNewCommentInputShown && 'has-comment-input',
  ].filter(Boolean).join(' ') || undefined;
  return (
    <>
      <td
        className={lineNumberClasses}
        data-line-number={lineNumber}
        data-path={path}
        data-side={side}>
        {lineNumber}
      </td>
      <td className={contentClassName}>
        {canAddComment && lineNumber != null && (
          <span
            className="diff-add-comment-button"
            data-add-comment="true"
          />
        )}
        {content}
        {commentThreads}
        {input}
      </td>
    </>
  );
}

function SplitDiffRowCommentThreads({
  line,
  threadsByLine,
}: {
  line: number;
  threadsByLine: GitHubPullRequestReviewThreadsByLine;
}): React.ReactElement | null {
  const threads = threadsByLine.get(line);
  if (threads == null) {
    return null;
  }

  return (
    <>
      {threads.map((thread, index) => {
        // Add a prefix to keys for this component to ensure they are distinct
        // from the integer keys returned by createTokenizedIntralineDiff().
        const key = `c-${index}`;
        return <InlineCommentThread key={key} thread={thread} />;
      })}
    </>
  );
}

/** Reads the global cell atom and renders the comment input.
 *  Extracted so only the ONE line with an active input subscribes to the cell atom,
 *  rather than every SplitDiffRowSide instance. */
function NewCommentInputWithCell({line, path, side}: {line: number; path: string; side: DiffSide}) {
  const cell = useAtomValue(gitHubPullRequestNewCommentInputCellAtom);
  return (
    <PullRequestNewCommentInput
      line={line}
      path={path}
      side={side}
      startLine={cell?.startLine}
      startSide={cell?.startSide}
    />
  );
}
